import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import fetchApi from '@/utils/fetchApi'
import type { Period, PeriodRange } from '@/types/Common'
import type {
  ChartInterval,
  ColdMonitoringCallDetailsResponse,
  ColdMonitoringChartsResponse,
  ColdMonitoringKPI,
  ColdMonitoringQuery,
  ColdMonitoringResponse,
  DisconnectedBy,
  VaLanguage,
} from 'shared'

interface UseVolumeCallsOptions {
  period: Ref<PeriodRange | undefined>
  agents: Ref<string[] | undefined>
  chartPeriod?: Ref<Period>
  jwtToken?: Ref<string | undefined>
  backendUrl?: Ref<string | undefined>
}

export const useColdMonitoringApi = ({
  period,
  agents,
  chartPeriod,
  backendUrl,
}: UseVolumeCallsOptions) => {
  const loading = ref(false)
  const data = ref<unknown>(null)

  const queryParams = computed(() => {
    const p = period.value
    const params: Record<string, string> = {}

    if (p?.startDate) params.from = p.startDate
    if (p?.endDate) params.to = p.endDate
    if (p?.startTime) params.startTime = p.startTime
    if (p?.endTime) params.endTime = p.endTime
    if (chartPeriod?.value) params.period = chartPeriod.value
    if (agents.value?.length) params.agents = agents.value.join(',')

    return new URLSearchParams(params).toString()
  })

  const fetchVolumeCalls = async () => {
    loading.value = true
    try {
      data.value = await fetchApi({
        endpoint: `/volume-of-calls?${queryParams.value}`,
        method: 'GET',
        api: 'private',
        backendUrl: backendUrl?.value,
      })
    } finally {
      loading.value = false
    }
  }

  const fetchColdCalls = async (filters: ColdMonitoringQuery) => {
    loading.value = true

    try {
      const result = await fetchApi<ColdMonitoringResponse>({
        endpoint: `/api/cold-monitoring`,
        method: 'POST',
        body: buildColdQuery(filters),
      })
      return result
    } finally {
      loading.value = false
    }
  }

  const fetchColdStats = async (filters: ColdMonitoringQuery) => {
    loading.value = true
    try {
      const query = buildColdQuery(filters)

      const result = await fetchApi<{ kpis: ColdMonitoringKPI[] }>({
        endpoint: `/api/cold-monitoring/stats`,
        method: 'POST',
        body: query,
      })

      return result
    } finally {
      loading.value = false
    }
  }

  const fetchColdChart = async (
    chartId: string,
    filters: Partial<{
      agents: string[]
      allBut: boolean
      languages: VaLanguage[]
      disconnectedBy: DisconnectedBy[]
      datetimeFrom: number
      datetimeTo: number
      chartInterval: ChartInterval
      includeLiveTests: boolean
    }>,
  ) => {
    loading.value = true
    try {
      const body: Record<string, unknown> = {}
      if (filters.datetimeFrom) body.datetimeFrom = filters.datetimeFrom
      if (filters.datetimeTo) body.datetimeTo = filters.datetimeTo
      if (filters.chartInterval) body.chartInterval = filters.chartInterval
      if (filters.agents?.length) body.agents = filters.agents
      if (filters.allBut) body.allBut = filters.allBut
      if (filters.languages?.length) body.languages = filters.languages
      if (filters.disconnectedBy?.length) body.disconnectedBy = filters.disconnectedBy
      if (filters.includeLiveTests !== undefined) body.includeLiveTests = filters.includeLiveTests

      return await fetchApi<ColdMonitoringChartsResponse>({
        endpoint: `/api/cold-monitoring/charts/${chartId}`,
        method: 'POST',
        body,
      })
    } catch (err) {
      console.error('Error while fetching chart data', err)
    } finally {
      loading.value = false
    }
  }

  const fetchColdCallDetails = async (callId: string, options?: { suppressAlert?: boolean }) => {
    loading.value = true
    try {
      const result = await fetchApi<ColdMonitoringCallDetailsResponse>(
        {
          endpoint: `/api/cold-monitoring/${callId}/details`,
          method: 'GET',
        },
        { suppressAlert: options?.suppressAlert },
      )
      return result
    } finally {
      loading.value = false
    }
  }

  const buildColdQuery = (filters: ColdMonitoringQuery) => {
    const query: ColdMonitoringQuery = {}
    if (filters.page) query.page = filters.page
    if (filters.pageSize) query.pageSize = filters.pageSize
    if (filters.datetimeFrom) query.datetimeFrom = filters.datetimeFrom
    if (filters.datetimeTo) query.datetimeTo = filters.datetimeTo

    if (filters.agents?.length) query.agents = filters.agents
    if (filters.allBut) query.allBut = filters.allBut
    if (filters.languages?.length) query.languages = filters.languages
    if (filters.disconnectedBy?.length) query.disconnectedBy = filters.disconnectedBy
    if (filters.sortBy) query.sortBy = filters.sortBy
    if (filters.sortDirection) query.sortDirection = filters.sortDirection
    if (filters.includeLiveTests !== undefined) query.includeLiveTests = filters.includeLiveTests
    return query as Record<string, unknown>
  }

  const debouncedFetch = useDebounceFn(async () => {
    try {
      if (period.value?.startDate && period.value.endDate) {
        await fetchVolumeCalls()
      }
    } catch (err) {
      console.error('Debounced fetch failed:', err)
    }
  }, 300)

  // Only watch if chartPeriod is provided
  if (chartPeriod) {
    watch(
      [agents, chartPeriod],
      () => {
        void debouncedFetch()
      },
      { immediate: true, deep: true },
    )
  }

  return {
    data,
    fetchVolumeCalls,
    fetchColdCalls,
    fetchColdStats,
    fetchColdChart,
    fetchColdCallDetails,
    loading,
  }
}
